import React from 'react';
import { Star, Quote } from 'lucide-react';

// 8 testimonials — men and women interleaved across two rows for a natural
// mix. Copy is compliance-safe: quality, purity, COAs, packaging, shipping
// and service. Never personal-use effects (Hydro Health = research market).
const TESTIMONIALS = [
  // ---------- ROW 1 (M / W / M / W) ----------
  {
    id: 1,
    name: 'James M.',
    role: 'Research Lab Manager',
    location: 'United Kingdom',
    quote:
      'The COA matched every batch to the letter. Independent third-party verification is exactly what our lab needs — no more chasing paperwork from suppliers.',
    image: '/testimonials/face-1.jpg',
  },
  {
    id: 5,
    name: 'Sarah L.',
    role: 'Molecular Biologist',
    location: 'United States',
    quote:
      'The extended analytical panel is what sold me. Heavy metals, endotoxins, microbial safety — the full picture, not just a purity number on a page.',
    image: '/testimonials/face-5.jpg',
  },
  {
    id: 3,
    name: 'Marcus T.',
    role: 'Compounding Pharmacist',
    location: 'Germany',
    quote:
      'Endotoxin levels and vial sealing are on point. Reconstitution behaves exactly as specified. Hydro Health is my default supplier now.',
    image: '/testimonials/face-3.jpg',
  },
  {
    id: 7,
    name: 'Grace O.',
    role: 'Research Chemist',
    location: 'Canada',
    quote:
      'Vials arrive properly sealed with the crimp intact every single time. Reconstitution is clean and consistent — that reliability matters for our data.',
    image: '/testimonials/face-7.jpg',
  },

  // ---------- ROW 2 (W / M / W / M) ----------
  {
    id: 6,
    name: 'Mei H.',
    role: 'Lab Coordinator',
    location: 'Singapore',
    quote:
      'Batches are consistent, packaging is professional, and the COA is emailed before the parcel even ships. Rare to find that level of care.',
    image: '/testimonials/face-6.jpg',
  },
  {
    id: 2,
    name: 'Daniel R.',
    role: 'Sports Science',
    location: 'Ireland',
    quote:
      'Consistently high purity across every order I have tested. Packaging is discreet and the tracked shipping actually arrives when it says it will.',
    image: '/testimonials/face-2.jpg',
  },
  {
    id: 8,
    name: 'Isabel R.',
    role: 'Independent Researcher',
    location: 'Spain',
    quote:
      'Support answered my batch-number query within the hour and sent the matching COA on the spot. Zero friction from order through to delivery.',
    image: '/testimonials/face-8.jpg',
  },
  {
    id: 4,
    name: 'Aiden K.',
    role: 'Research Buyer',
    location: 'Australia',
    quote:
      'Fast responses, transparent COAs, and every vial arrives sealed and labelled correctly. Best-in-class service from order to delivery.',
    image: '/testimonials/face-4.jpg',
  },
];

/**
 * TestimonialsBlock — 8-card social-proof grid.
 * Two rows of 4 on desktop, with men and women interleaved to feel natural
 * and inclusive. Compliance-safe copy (research market only).
 */
const TestimonialsBlock = ({ compact = false }) => (
  <section
    data-testid="testimonials-block"
    className={`bg-black ${compact ? 'py-16' : 'py-20'}`}
  >
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <p className="text-[#909090] text-sm uppercase tracking-widest mb-3">
          Trusted by labs worldwide
        </p>
        <h2 className="text-3xl md:text-4xl font-bold">
          What our customers say
        </h2>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TESTIMONIALS.map((t) => (
          <figure
            key={t.id}
            data-testid={`testimonial-card-${t.id}`}
            className="relative bg-zinc-950 border border-zinc-900 hover:border-[#909090]/60 transition-colors rounded-md p-6 flex flex-col"
          >
            <Quote
              className="absolute top-4 right-4 text-[#909090]/25"
              size={40}
              strokeWidth={1.5}
            />

            <div className="flex items-center gap-1 text-[#c9c9c9] mb-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
              ))}
            </div>

            <blockquote className="text-zinc-300 text-sm leading-relaxed flex-1">
              &ldquo;{t.quote}&rdquo;
            </blockquote>

            <figcaption className="mt-6 pt-5 border-t border-zinc-900 flex items-center gap-3">
              <img
                src={t.image}
                alt={t.name}
                loading="lazy"
                className="w-11 h-11 rounded-full object-cover border border-zinc-800 bg-zinc-900"
              />
              <div>
                <p className="text-white text-sm font-semibold leading-tight">
                  {t.name}
                </p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  {t.role} · {t.location}
                </p>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

export default TestimonialsBlock;
